import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Loader2, Plus, Pencil, Trash2, X, Download, Eye, FileText, Check, Upload, Search,
} from 'lucide-react';
import { API_URL } from './shared';

/**
 * Supplier Invoice Filing — upload, store and track bills we RECEIVE from
 * suppliers. Separate from the customer-facing invoices in InvoicesTab.
 * Lives at Admin → Supplier Invoices (sits right next to Invoices).
 */
const EMPTY_FORM = {
  supplier_name: '',
  invoice_number: '',
  invoice_date: '',
  amount: '',
  status: 'received',
  paid_date: '',
  notes: '',
};

const StatusPill = ({ status }) => (
  <Badge
    className={
      status === 'paid'
        ? 'bg-green-100 text-green-800 hover:bg-green-100'
        : 'bg-amber-100 text-amber-900 hover:bg-amber-100'
    }
    data-testid={`si-status-${status}`}
  >
    {status === 'paid' ? 'Paid' : 'Received'}
  </Badge>
);

const SupplierInvoicesTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ received: { count: 0, amount: 0 }, paid: { count: 0, amount: 0 } });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | received | paid
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (query.trim()) params.q = query.trim();
      const r = await axios.get(`${API_URL}/admin/supplier-invoices`, { ...authHeaders, params });
      setItems(r.data.items || []);
      setTotals(r.data.totals || { received: { count: 0, amount: 0 }, paid: { count: 0, amount: 0 } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load supplier invoices');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, query, token]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFile(null);
    setModalOpen(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      supplier_name: inv.supplier_name || '',
      invoice_number: inv.invoice_number || '',
      invoice_date: inv.invoice_date || '',
      amount: inv.amount != null ? String(inv.amount) : '',
      status: inv.status || 'received',
      paid_date: inv.paid_date || '',
      notes: inv.notes || '',
    });
    setFile(null);
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_name.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        // Edit = PATCH metadata (file stays the same)
        const payload = {
          supplier_name: form.supplier_name,
          invoice_number: form.invoice_number,
          invoice_date: form.invoice_date,
          amount: Number(form.amount || 0),
          status: form.status,
          paid_date: form.status === 'paid' ? form.paid_date : null,
          notes: form.notes,
        };
        await axios.patch(`${API_URL}/admin/supplier-invoices/${editing.id}`, payload, authHeaders);
        toast.success('Invoice updated');
      } else {
        if (!file) {
          toast.error('Attach the invoice file');
          setSaving(false);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error('File must be under 10 MB');
          setSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append('supplier_name', form.supplier_name);
        fd.append('invoice_number', form.invoice_number || '');
        fd.append('invoice_date', form.invoice_date || '');
        fd.append('amount', String(Number(form.amount || 0)));
        fd.append('status', form.status || 'received');
        fd.append('paid_date', form.status === 'paid' ? (form.paid_date || '') : '');
        fd.append('notes', form.notes || '');
        fd.append('file', file);
        await axios.post(`${API_URL}/admin/supplier-invoices`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Invoice filed');
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePaid = async (inv) => {
    const next = inv.status === 'paid' ? 'received' : 'paid';
    setBusyId(inv.id);
    try {
      await axios.patch(
        `${API_URL}/admin/supplier-invoices/${inv.id}`,
        { status: next },
        authHeaders,
      );
      toast.success(next === 'paid' ? 'Marked paid' : 'Marked received');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const removeInvoice = async (inv) => {
    if (!window.confirm(`Delete the record for "${inv.supplier_name}${inv.invoice_number ? ` · ${inv.invoice_number}` : ''}"? The stored file is retained for compliance.`)) return;
    setBusyId(inv.id);
    try {
      await axios.delete(`${API_URL}/admin/supplier-invoices/${inv.id}`, authHeaders);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  // Fetches the stored file with the admin bearer token and opens it in a
  // new tab. Needed because the endpoint is auth-protected; a plain <a href>
  // would 401.
  const openFile = async (inv) => {
    try {
      const r = await axios.get(
        `${API_URL}/admin/supplier-invoices/${inv.id}/file`,
        { ...authHeaders, responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      // Revoke after a short delay so the new tab has time to pick it up.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open file');
    }
  };

  const downloadFile = async (inv) => {
    try {
      const r = await axios.get(
        `${API_URL}/admin/supplier-invoices/${inv.id}/file`,
        { ...authHeaders, responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = inv.file_name || `invoice-${inv.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const formatR = (n) => `R${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4" data-testid="supplier-invoices-tab">
      {/* Header + totals */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#0A2342]">Supplier Invoices</h2>
          <p className="text-sm text-gray-600">Store the bills you receive from suppliers — mark them paid once settled.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm" data-testid="si-total-received">
            <div className="text-amber-900 font-semibold">Awaiting payment</div>
            <div className="text-amber-800">{totals.received.count} · {formatR(totals.received.amount)}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm" data-testid="si-total-paid">
            <div className="text-green-900 font-semibold">Paid</div>
            <div className="text-green-800">{totals.paid.count} · {formatR(totals.paid.amount)}</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={openCreate} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="si-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Upload invoice
        </Button>
        <div className="flex items-center gap-1 border rounded-md p-1 bg-white">
          {['all', 'received', 'paid'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm rounded ${filter === f ? 'bg-[#0A2342] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid={`si-filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'received' ? 'Awaiting payment' : 'Paid'}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search supplier, invoice number, notes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="si-search"
          />
        </div>
      </div>

      {/* List */}
      <div className="border rounded-lg overflow-hidden bg-white">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="font-medium">No supplier invoices yet</p>
            <p className="text-xs mt-1">Click <strong>Upload invoice</strong> above to file your first one.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="p-3">Supplier</th>
                <th className="p-3">Invoice #</th>
                <th className="p-3">Invoice date</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3">Status</th>
                <th className="p-3">Paid on</th>
                <th className="p-3">File</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} className="border-t hover:bg-gray-50" data-testid={`si-row-${inv.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-[#0A2342]">{inv.supplier_name}</div>
                    {inv.notes && <div className="text-xs text-gray-500 line-clamp-1 max-w-[260px]">{inv.notes}</div>}
                  </td>
                  <td className="p-3 text-gray-700">{inv.invoice_number || '—'}</td>
                  <td className="p-3 text-gray-700">{inv.invoice_date || '—'}</td>
                  <td className="p-3 text-right font-semibold text-gray-900">{formatR(inv.amount)}</td>
                  <td className="p-3"><StatusPill status={inv.status} /></td>
                  <td className="p-3 text-gray-700">{inv.status === 'paid' ? (inv.paid_date || '—') : '—'}</td>
                  <td className="p-3">
                    <button
                      onClick={() => openFile(inv)}
                      className="inline-flex items-center gap-1 text-[#0A2342] hover:underline text-xs"
                      data-testid={`si-view-file-${inv.id}`}
                    >
                      <Eye className="w-3 h-3" />
                      <span className="truncate max-w-[140px]">{inv.file_name || 'View'}</span>
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => togglePaid(inv)}
                        disabled={busyId === inv.id}
                        className={inv.status === 'paid' ? 'text-amber-700 hover:bg-amber-50' : 'text-green-700 hover:bg-green-50'}
                        data-testid={`si-toggle-paid-${inv.id}`}
                      >
                        {busyId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (inv.status === 'paid' ? 'Undo paid' : <><Check className="w-3 h-3 mr-1" /> Mark paid</>)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadFile(inv)} data-testid={`si-download-${inv.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(inv)} data-testid={`si-edit-${inv.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeInvoice(inv)} disabled={busyId === inv.id} data-testid={`si-delete-${inv.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload / edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-xl w-full p-6 shadow-xl" data-testid="si-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0A2342]">
                {editing ? 'Edit supplier invoice' : 'Upload supplier invoice'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Supplier / vendor *</label>
                <Input
                  value={form.supplier_name}
                  onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                  placeholder="e.g. Mares SA"
                  required
                  data-testid="si-form-supplier"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Invoice number</label>
                  <Input
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    placeholder="INV-00123"
                    data-testid="si-form-number"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Invoice date</label>
                  <Input
                    type="date"
                    value={form.invoice_date}
                    onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                    data-testid="si-form-date"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Amount (R)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    data-testid="si-form-amount"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full border rounded-md p-2 text-sm bg-white"
                    data-testid="si-form-status"
                  >
                    <option value="received">Received (awaiting payment)</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              {form.status === 'paid' && (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Paid on</label>
                  <Input
                    type="date"
                    value={form.paid_date}
                    onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
                    data-testid="si-form-paid-date"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Leave blank — we'll auto-fill today's date on save.</p>
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-700 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="PO reference, delivery note, anything your accountant needs…"
                  className="w-full border rounded-md p-2 text-sm"
                  data-testid="si-form-notes"
                />
              </div>
              {!editing && (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">File attachment *</label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="hidden"
                      data-testid="si-form-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="si-form-file-pick"
                    >
                      <Upload className="w-4 h-4 mr-1" /> {file ? 'Choose different' : 'Pick file'}
                    </Button>
                    <span className="text-sm text-gray-600 truncate">
                      {file ? `${file.name} (${(file.size / 1024).toFixed(0)} KB)` : 'PDF / JPG / PNG / DOC / XLS — max 10 MB'}
                    </span>
                  </div>
                </div>
              )}
              {editing && (
                <div className="bg-gray-50 border rounded-md p-3 text-xs text-gray-600">
                  <strong>File:</strong> {editing.file_name}
                  <br />
                  To replace the file itself, delete this record and upload again (metadata is tied to the stored bytes).
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="si-form-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {editing ? 'Save changes' : 'Upload & file'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierInvoicesTab;
